<template>
  <div class="container-fluid py-4">
    <!-- Encabezado -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
        <i class="bx bx-wrench text-amber-500 text-3xl"></i>
        Herramientas de Mantenimiento
      </h1>
      <p class="text-slate-500 dark:text-slate-400 mt-1 text-sm">
        Operaciones de limpieza y liberación de datos. Solo visible para Administradores.
        Estas acciones afectan la base de datos de forma permanente.
      </p>
    </div>

    <!-- Alerta global de advertencia -->
    <div class="alert alert-warning d-flex align-items-center gap-2 mb-4" role="alert">
      <i class="bx bx-error-circle text-xl flex-shrink-0"></i>
      <div>
        <strong>¡Atención!</strong> Estas operaciones son irreversibles. Utilícelas únicamente
        cuando esté seguro de que no afectarán operaciones activas del hotel o restaurante.
      </div>
    </div>

    <!-- Grid de tarjetas de acciones -->
    <div class="row g-4">

      <!-- 1. Liberar Habitaciones -->
      <div class="col-12 col-md-6 col-xl-4">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-body d-flex flex-column p-4">
            <div class="d-flex align-items-center mb-3">
              <div class="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                style="width:48px;height:48px;background:rgba(34,197,94,0.12);">
                <i class="bx bx-door-open text-2xl text-green-600"></i>
              </div>
              <div>
                <h5 class="card-title mb-0 fw-bold">Liberar Habitaciones</h5>
                <small class="text-muted">Hotel</small>
              </div>
            </div>
            <p class="card-text text-slate-600 dark:text-slate-400 text-sm flex-grow-1">
              Pone todas las habitaciones ocupadas, en espera de limpieza o con limpieza realizada
              al estado <strong>Disponible</strong>. No afecta a las habitaciones en mantenimiento.
            </p>
            <button
              @click="confirmar('habitaciones')"
              :disabled="cargando.habitaciones"
              class="btn btn-success mt-3 d-flex align-items-center justify-content-center gap-2"
            >
              <span v-if="cargando.habitaciones" class="spinner-border spinner-border-sm"></span>
              <i v-else class="bx bx-door-open"></i>
              Liberar todas las habitaciones
            </button>
            <div v-if="resultados.habitaciones" class="mt-2 alert alert-success py-2 mb-0 small">
              {{ resultados.habitaciones }}
            </div>
          </div>
        </div>
      </div>

      <!-- 2. Liberar Reservas (Checkout masivo) -->
      <div class="col-12 col-md-6 col-xl-4">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-body d-flex flex-column p-4">
            <div class="d-flex align-items-center mb-3">
              <div class="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                style="width:48px;height:48px;background:rgba(59,130,246,0.12);">
                <i class="bx bx-calendar-check text-2xl text-blue-600"></i>
              </div>
              <div>
                <h5 class="card-title mb-0 fw-bold">Checkout de Reservas</h5>
                <small class="text-muted">Hotel</small>
              </div>
            </div>
            <p class="card-text text-slate-600 dark:text-slate-400 text-sm flex-grow-1">
              Realiza el <strong>check-out</strong> de todas las reservas activas aunque no
              hayan sido pagadas. Marca las reservas como <em>Finalizadas</em> y pone las
              habitaciones en <em>Disponible</em>.
            </p>
            <button
              @click="confirmar('reservas')"
              :disabled="cargando.reservas"
              class="btn btn-primary mt-3 d-flex align-items-center justify-content-center gap-2"
            >
              <span v-if="cargando.reservas" class="spinner-border spinner-border-sm"></span>
              <i v-else class="bx bx-calendar-check"></i>
              Checkout de todas las reservas
            </button>
            <div v-if="resultados.reservas" class="mt-2 alert alert-success py-2 mb-0 small">
              {{ resultados.reservas }}
            </div>
          </div>
        </div>
      </div>

      <!-- 3. Limpiar Pedidos Caja / Restaurante -->
      <div class="col-12 col-md-6 col-xl-4">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-body d-flex flex-column p-4">
            <div class="d-flex align-items-center mb-3">
              <div class="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                style="width:48px;height:48px;background:rgba(234,179,8,0.12);">
                <i class="bx bx-table text-2xl text-yellow-600"></i>
              </div>
              <div>
                <h5 class="card-title mb-0 fw-bold">Limpiar Caja Restaurante</h5>
                <small class="text-muted">Restaurante · /cuentas-pendientes-mesas</small>
              </div>
            </div>
            <p class="card-text text-slate-600 dark:text-slate-400 text-sm flex-grow-1">
              Cierra todos los <strong>pedidos abiertos</strong> y <strong>cuentas pendientes</strong>
              del restaurante aunque no se hayan cobrado. También libera todas las mesas ocupadas.
            </p>
            <button
              @click="confirmar('pedidos')"
              :disabled="cargando.pedidos"
              class="btn btn-warning mt-3 d-flex align-items-center justify-content-center gap-2"
            >
              <span v-if="cargando.pedidos" class="spinner-border spinner-border-sm"></span>
              <i v-else class="bx bx-table"></i>
              Limpiar todos los pedidos de caja
            </button>
            <div v-if="resultados.pedidos" class="mt-2 alert alert-success py-2 mb-0 small">
              {{ resultados.pedidos }}
            </div>
          </div>
        </div>
      </div>

      <!-- 4. Limpiar Impresiones de Comandas -->
      <div class="col-12 col-md-6 col-xl-4">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-body d-flex flex-column p-4">
            <div class="d-flex align-items-center mb-3">
              <div class="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                style="width:48px;height:48px;background:rgba(168,85,247,0.12);">
                <i class="bx bx-printer text-2xl text-purple-600"></i>
              </div>
              <div>
                <h5 class="card-title mb-0 fw-bold">Limpiar Historial Comandas</h5>
                <small class="text-muted">Restaurante · Historial Impresiones</small>
              </div>
            </div>
            <p class="card-text text-slate-600 dark:text-slate-400 text-sm flex-grow-1">
              Elimina todos los registros del historial de <strong>impresiones de comandas</strong>
              (cocina/barra). Los pedidos en sí no se borran, solo los registros de impresión.
            </p>
            <button
              @click="confirmar('impresiones')"
              :disabled="cargando.impresiones"
              class="btn btn-secondary mt-3 d-flex align-items-center justify-content-center gap-2"
            >
              <span v-if="cargando.impresiones" class="spinner-border spinner-border-sm"></span>
              <i v-else class="bx bx-printer"></i>
              Limpiar historial de comandas
            </button>
            <div v-if="resultados.impresiones" class="mt-2 alert alert-success py-2 mb-0 small">
              {{ resultados.impresiones }}
            </div>
          </div>
        </div>
      </div>

      <!-- 5. Liberar Mesas -->
      <div class="col-12 col-md-6 col-xl-4">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-body d-flex flex-column p-4">
            <div class="d-flex align-items-center mb-3">
              <div class="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                style="width:48px;height:48px;background:rgba(239,68,68,0.12);">
                <i class="bx bx-chair text-2xl text-red-500"></i>
              </div>
              <div>
                <h5 class="card-title mb-0 fw-bold">Liberar Mesas</h5>
                <small class="text-muted">Restaurante</small>
              </div>
            </div>
            <p class="card-text text-slate-600 dark:text-slate-400 text-sm flex-grow-1">
              Pone todas las mesas ocupadas en estado <strong>Libre</strong>.
              No afecta las mesas marcadas como <em>Fuera de servicio</em>.
            </p>
            <button
              @click="confirmar('mesas')"
              :disabled="cargando.mesas"
              class="btn btn-danger mt-3 d-flex align-items-center justify-content-center gap-2"
            >
              <span v-if="cargando.mesas" class="spinner-border spinner-border-sm"></span>
              <i v-else class="bx bx-chair"></i>
              Liberar todas las mesas
            </button>
            <div v-if="resultados.mesas" class="mt-2 alert alert-success py-2 mb-0 small">
              {{ resultados.mesas }}
            </div>
          </div>
        </div>
      </div>

      <!-- 6. Soft Reboot Completo -->
      <div class="col-12 col-xl-4">
        <div class="card h-100 shadow-sm" style="border: 1px solid #fca5a5 !important;">
          <div class="card-body d-flex flex-column p-4">
            <div class="d-flex align-items-center mb-3">
              <div class="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                style="width:48px;height:48px;background:rgba(239,68,68,0.18);">
                <i class="bx bx-reset text-2xl text-red-600"></i>
              </div>
              <div>
                <h5 class="card-title mb-0 fw-bold text-red-600">Soft Reboot Completo</h5>
                <small class="text-danger fw-semibold">Acción crítica — Todo a la vez</small>
              </div>
            </div>
            <p class="card-text text-slate-600 dark:text-slate-400 text-sm flex-grow-1">
              Ejecuta <strong>todas las operaciones anteriores</strong> en una sola acción:
              libera habitaciones, hace checkout de reservas, cierra pedidos y cuentas,
              libera mesas y limpia el historial de comandas.
            </p>
            <button
              @click="confirmar('reboot')"
              :disabled="cargando.reboot"
              class="btn btn-danger mt-3 d-flex align-items-center justify-content-center gap-2"
              style="background-color: #dc2626;"
            >
              <span v-if="cargando.reboot" class="spinner-border spinner-border-sm"></span>
              <i v-else class="bx bx-reset"></i>
              Ejecutar Soft Reboot Completo
            </button>
            <div v-if="resultados.reboot" class="mt-2 alert alert-success py-2 mb-0 small">
              <pre class="mb-0" style="white-space:pre-wrap;font-size:0.75rem;">{{ resultados.reboot }}</pre>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Modal de confirmación -->
    <div
      v-if="modalConfirmacion.visible"
      class="modal fade show d-block"
      tabindex="-1"
      style="background:rgba(0,0,0,0.5);"
      @click.self="modalConfirmacion.visible = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title d-flex align-items-center gap-2">
              <i class="bx bx-error"></i> Confirmar Acción
            </h5>
            <button type="button" class="btn-close btn-close-white" @click="modalConfirmacion.visible = false"></button>
          </div>
          <div class="modal-body">
            <p class="mb-2">¿Está seguro que desea ejecutar:</p>
            <p class="fw-bold text-danger fs-6">{{ modalConfirmacion.titulo }}</p>
            <p class="text-muted small">{{ modalConfirmacion.descripcion }}</p>
            <div class="alert alert-warning py-2 mb-0 small">
              <i class="bx bx-error-circle me-1"></i>
              Esta acción <strong>no se puede deshacer</strong>.
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="modalConfirmacion.visible = false">Cancelar</button>
            <button class="btn btn-danger d-flex align-items-center gap-2" @click="ejecutarAccion">
              <i class="bx bx-check"></i> Sí, ejecutar
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '@/config/config';

const ACCIONES = {
  habitaciones: {
    titulo:      'Liberar Todas las Habitaciones',
    descripcion: 'Todas las habitaciones (excepto mantenimiento) quedarán como Disponibles.',
    metodo:      'post',
    url:         `${API_URL}/admin/sistema/liberar-habitaciones`,
    formatRespuesta: (d) => d.message,
  },
  reservas: {
    titulo:      'Checkout de Todas las Reservas Activas',
    descripcion: 'Se finalizarán todas las reservas aunque no estén pagadas.',
    metodo:      'post',
    url:         `${API_URL}/admin/sistema/liberar-reservas`,
    formatRespuesta: (d) => d.message,
  },
  pedidos: {
    titulo:      'Limpiar Todos los Pedidos de Caja',
    descripcion: 'Se cerrarán todas las cuentas y pedidos pendientes del restaurante y se liberarán las mesas.',
    metodo:      'post',
    url:         `${API_URL}/admin/sistema/limpiar-pedidos-caja`,
    formatRespuesta: (d) => d.message,
  },
  impresiones: {
    titulo:      'Limpiar Historial de Impresiones de Comandas',
    descripcion: 'Se borrarán todos los registros de impresión de comandas (cocina y barra).',
    metodo:      'delete',
    url:         `${API_URL}/admin/sistema/limpiar-impresiones`,
    formatRespuesta: (d) => d.message,
  },
  mesas: {
    titulo:      'Liberar Todas las Mesas',
    descripcion: 'Todas las mesas ocupadas quedarán como Libres (excepto las Fuera de Servicio).',
    metodo:      'post',
    url:         `${API_URL}/admin/sistema/liberar-mesas`,
    formatRespuesta: (d) => d.message,
  },
  reboot: {
    titulo:      'Soft Reboot Completo del Sistema',
    descripcion: 'Se ejecutarán TODAS las operaciones anteriores a la vez: habitaciones, reservas, pedidos, mesas e impresiones.',
    metodo:      'post',
    url:         `${API_URL}/admin/sistema/soft-reboot`,
    formatRespuesta: (d) => {
      if (!d.detalle) return d.message;
      const det = d.detalle;
      return [
        d.message,
        `• Habitaciones liberadas: ${det.habitaciones_liberadas}`,
        `• Habitaciones con checkout: ${det.habitaciones_con_checkout}`,
        `• Reservas cerradas: ${det.reservas_cerradas}`,
        `• Cuentas cerradas: ${det.cuentas_cerradas}`,
        `• Pedidos cerrados: ${det.pedidos_cerrados}`,
        `• Mesas liberadas: ${det.mesas_liberadas}`,
        `• Impresiones eliminadas: ${det.impresiones_eliminadas}`,
      ].join('\n');
    },
  },
};

export default {
  name: 'PaginaExtras',
  data() {
    return {
      cargando: {
        habitaciones: false,
        reservas:     false,
        pedidos:      false,
        impresiones:  false,
        mesas:        false,
        reboot:       false,
      },
      resultados: {
        habitaciones: '',
        reservas:     '',
        pedidos:      '',
        impresiones:  '',
        mesas:        '',
        reboot:       '',
      },
      modalConfirmacion: {
        visible:     false,
        accion:      null,
        titulo:      '',
        descripcion: '',
      },
    };
  },
  methods: {
    confirmar(accion) {
      const cfg = ACCIONES[accion];
      this.modalConfirmacion = {
        visible:     true,
        accion,
        titulo:      cfg.titulo,
        descripcion: cfg.descripcion,
      };
    },
    async ejecutarAccion() {
      const accion = this.modalConfirmacion.accion;
      this.modalConfirmacion.visible = false;

      if (!accion || !ACCIONES[accion]) return;

      const cfg = ACCIONES[accion];
      this.cargando[accion] = true;
      this.resultados[accion] = '';

      try {
        let response;
        if (cfg.metodo === 'delete') {
          response = await axios.delete(cfg.url);
        } else {
          response = await axios.post(cfg.url);
        }
        this.resultados[accion] = cfg.formatRespuesta(response.data);
      } catch (error) {
        const msg = error.response?.data?.message || error.message || 'Error desconocido';
        this.resultados[accion] = '⚠️ Error: ' + msg;
      } finally {
        this.cargando[accion] = false;
      }
    },
  },
};
</script>
